/* eslint-disable consistent-return */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Align, Chart as ChartJS, TitleOptions } from 'chart.js';

import { FormatProperties } from 'models/response/modelOutput.interface';

import formatValue, { getSmartDecimalPlaces } from './FormatValues';

ChartJS.register(ChartDataLabels);

export interface PositionLegend {
  position: TitleOptions['position'];
  align: Align;
}

interface Properties {
  chartFormatting?: any;
  chartType?: string;
  isLegend?: boolean;
  pageWidth?: number;
  formatProperties?: FormatProperties;
  pageName?: string;
  aspectRatio: number;
  pageHeight?: number;
  legendPosition?: PositionLegend;
  hasPercentageDataType?: boolean;
}

const UNIT_MULTIPLIERS: any = {
  k: 1000,
  mn: 1_000_000,
  bn: 1_000_000_000,
  tn: 1_000_000_000_000,
  auto: 1,
};

const formatYAxisValue = (data: string): string => {
  if (data === null || data === undefined) {
    return String(data);
  }

  const parsedValue = Number(data);
  if (!Number.isFinite(parsedValue)) {
    return String(data);
  }
  const isNegative = parsedValue < 0;

  // Use the absolute value for formatting
  const value = Math.abs(parsedValue);
  const formatCompact = (number_: number, divisor: number, suffix: string) => {
    const scaled = number_ / divisor;
    const dp = getSmartDecimalPlaces(scaled);
    return `${isNegative ? '-' : ''}${scaled.toLocaleString('en-GB', {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    })}${suffix}`;
  };

  if (value >= UNIT_MULTIPLIERS.tn) {
    return formatCompact(value, UNIT_MULTIPLIERS.tn, ' T');
  }
  if (value >= UNIT_MULTIPLIERS.bn) {
    return formatCompact(value, UNIT_MULTIPLIERS.bn, ' B');
  }
  if (value >= UNIT_MULTIPLIERS.mn) {
    return formatCompact(value, UNIT_MULTIPLIERS.mn, ' M');
  }
  if (value >= UNIT_MULTIPLIERS.k) {
    return formatCompact(value, UNIT_MULTIPLIERS.k, ' K');
  }

  // Handle small or negative numbers
  const dp = getSmartDecimalPlaces(value);
  const formattedValue = value.toLocaleString('en-GB', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
  return isNegative ? `-${formattedValue}` : formattedValue;
};

const getDashboradChartOptions = ({
  chartFormatting,
  isLegend = false,
  pageWidth = 0,
  pageHeight = 0,
  formatProperties,
  pageName = '',
  aspectRatio,
  legendPosition = { position: 'top' as TitleOptions['position'], align: 'end' as Align },
  hasPercentageDataType = false,
}: Properties) => {
  const formatWithSmartDecimals = (number_: number) => {
    const dp = getSmartDecimalPlaces(number_);
    return number_.toLocaleString('en-GB', {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    });
  };
  const formatDataLabel = (value: any) => {
    // let total: any = 0;
    // context.chart.data.datasets.forEach((dataset: any) => {
    //   total += dataset.data[context.dataIndex];
    // });
    if (chartFormatting?.units === 'auto') {
      return `${formatWithSmartDecimals(Number(value))}`;
    }

    const scaled = Number(value) / UNIT_MULTIPLIERS[chartFormatting?.units];
    return `${formatWithSmartDecimals(scaled)}${chartFormatting?.units}`;
  };

  return {
    aspectRatio,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: false },
      tooltip: {
        color: '#979797',
        callbacks: {
          label(context: any) {
            const label = context.dataset.label || '';
            const value = context.raw || context.formattedValue;

            if (formatProperties?.measure === '%') {
              const percentValue = Number(value) * 100;
              return `${label}: ${percentValue.toFixed(getSmartDecimalPlaces(percentValue))}%`;
            }

            if (chartFormatting?.units === 'auto') {
              return `${label}: ${formatWithSmartDecimals(Number(value))}`;
            }

            const scaled = Number(value) / UNIT_MULTIPLIERS[chartFormatting?.units];
            return `${label}: ${formatWithSmartDecimals(scaled)}${chartFormatting?.units}`;
          },
        },
      },
      legend: {
        display: isLegend,
        position: legendPosition.position,
        align: legendPosition.align,
        labels: {
          boxWidth: pageWidth < 576 ? 10 : 15,
          boxHeight: pageHeight < 576 ? 10 : 15,
          color: '#636363',
        },
      },
      datalabels: {
        display: !!chartFormatting?.displayValue,
        color: 'black',
        font: {
          size: 9,
        },
        align: 'end',
        anchor: 'end',
        position: 'top',
        formatter: formatDataLabel,
      },
    },
    scales: {
      x: {
        stacked: chartFormatting?.stackBar,
        grid: {
          color: 'transparent',
          borderColor: '#97979766',
        },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          color: '#212121',
          padding: 10,
        },
      },
      y: {
        beginAtZero: true,
        stacked: chartFormatting?.stackBar,
        grid: {
          color: '#97979766',
          borderColor: 'transparent',
          tickColor: 'transparent',
        },
        ticks: {
          color: '#212121',
          padding: 0,
          callback(value: any) {
            if (pageName === 'drivers') {
              if (formatProperties?.measure === '%') {
                return `${value * 100}%`;
              }
              return `${formatValue(value, formatProperties as FormatProperties)}`;
            }
            // If any indicator has data_type === "percentage", format with 2 decimal places
            if (hasPercentageDataType) {
              return Number(value).toFixed(getSmartDecimalPlaces(value));
            }
            return formatYAxisValue(value);
          },
        },
      },
    },
  };
};

export default getDashboradChartOptions;
